/**
 * Bidirectional route map for the Legacy <-> Phoenix UI switcher (PD-18256).
 * Mirrors marketplace's libs/web/core/config/src/routes/platform-switcher.config.ts.
 * No shared package between the two repos, so this must be kept in sync by
 * hand. Each key is one "intent" that exists as a route in both apps.
 *
 * Templates are bare path segments (no leading slash), Angular-style params
 * (`:id`).
 */
export const PLATFORM_SWITCHER_ROUTE_KEYS = [
  'dashboard',
  'createSurvey',
  'editSurvey',
  'manageSurvey',
  'launchSurvey',
] as const;

export type PlatformSwitcherRouteKey = (typeof PLATFORM_SWITCHER_ROUTE_KEYS)[number];

interface PlatformSwitcherRouteTemplate {
  legacy: string;
  phoenix: string;
}

export const PLATFORM_SWITCHER_ROUTE_MAP: Record<
  PlatformSwitcherRouteKey,
  PlatformSwitcherRouteTemplate
> = {
  // dashboard.module.ts -> { path: 'dashboard', children: [{ path: '' }] }
  dashboard: { legacy: 'dashboard', phoenix: 'dashboard' },
  // create-survey.module.ts -> { path: 'create-surveys' }
  createSurvey: { legacy: 'create-surveys', phoenix: 'surveys/new' },
  // create-survey.module.ts -> { path: 'surveys', children: [{ path: ':id/edit' }] }
  editSurvey: { legacy: 'surveys/:id/edit', phoenix: 'surveys/:id/edit' },
  // dashboard.module.ts -> { path: 'dashboard', children: [{ path: ':id' }] }
  manageSurvey: { legacy: 'dashboard/:id', phoenix: 'surveys/:id' },
  // buyer.module.ts -> { path: 'launch-survey/:id' }
  launchSurvey: { legacy: 'launch-survey/:id', phoenix: 'surveys/:id/launch' },
};

function replaceParam(
  path: string,
  param: string,
  value: string | number
): string {
  return path.replace(`:${param}`, String(value));
}

function interpolate(
  template: string,
  params: Record<string, string | number>
): string {
  return Object.entries(params).reduce(
    (path, [param, value]) => replaceParam(path, param, value),
    template
  );
}

export function toPhoenixPath(
  routeKey: PlatformSwitcherRouteKey,
  params: Record<string, string | number> = {}
): string {
  return `/${interpolate(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].phoenix, params)}`;
}

function toRegex(template: string): RegExp {
  const pattern = template.replace(/:([^/]+)/g, '(?<$1>[^/]+)');
  return new RegExp(`^/${pattern}/?$`);
}

export interface PlatformSwitcherRouteMatch {
  routeKey: PlatformSwitcherRouteKey;
  params: Record<string, string>;
}

/**
 * Reverse lookup: given the current Legacy pathname, finds which switcher
 * intent it maps to. Literal routes (e.g. `create-surveys`) must come
 * before their param siblings in PLATFORM_SWITCHER_ROUTE_KEYS, that ordering is
 * what keeps `:id` from swallowing a literal segment.
 */
export function matchLegacyPath(pathname: string): PlatformSwitcherRouteMatch | null {
  for (const routeKey of PLATFORM_SWITCHER_ROUTE_KEYS) {
    const match = toRegex(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].legacy).exec(
      pathname
    );
    if (match) {
      return { routeKey, params: match.groups ?? {} };
    }
  }
  return null;
}
